// The image's move, eased (Niki, 10/2: "a little bit jarring"). When the hand
// (useImageHand) or the arrow keys put an image somewhere new, the layout
// changes in one frame: the image jumps and the text after it jumps too.
// This plays that change as a short FLIP: everything is measured before the
// move and after it, then the image glides (and scales) from where it was,
// and the blocks around it slide from where they were, settling over ~300ms
// with an ease-out. The lines inside a paragraph rewrap at once (text can't
// tween), but the blocks they're in, and the photo, travel.
//
// While it plays, the rows carry a transform, so anything that measures the
// layout (the hand's slots) reads `layoutRect`, which takes the motion out.
// Reduced motion: no animation, the move happens at once as before.

/** How long the image takes to settle. */
export const FLIP_MS = 320
/** An ease-out: quick to leave, slow to land. */
export const FLIP_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)'
const FLIP_ID = 'wr-flip'

const figureOf = (id: string) =>
  Array.from(document.querySelectorAll<HTMLElement>('figure[data-selectable]')).find((f) => f.getAttribute('data-selectable') === id) ?? null

/**
 * The column's blocks as the hand sees them: every row and every Columns
 * group outside Columns (a group counts as one), in order. `root` is the
 * column (or the document).
 */
export function columnRows(root: ParentNode): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-row], [data-cols]')).filter((el) => !el.parentElement?.closest('[data-cols]'))
}

/** The translation an element is painted with now (an animation mid-flight), from its computed transform. */
function shiftOf(el: Element): { x: number; y: number } {
  const t = typeof getComputedStyle === 'function' ? getComputedStyle(el).transform : ''
  if (!t || t === 'none') return { x: 0, y: 0 }
  const m = /^matrix\(([^)]+)\)$/.exec(t)
  if (!m) return { x: 0, y: 0 }
  const v = m[1].split(',').map(Number)
  return { x: v[4] || 0, y: v[5] || 0 }
}

/**
 * Where `el` is laid out, without the motion it's painted with: its box,
 * less its own slide and (for something inside a row, like a paragraph)
 * its row's.
 */
export function layoutRect(el: Element): DOMRect {
  const r = el.getBoundingClientRect()
  let x = 0
  let y = 0
  for (let n: Element | null = el; n; n = n.parentElement?.closest('[data-row], [data-cols]') ?? null) {
    const s = shiftOf(n)
    x += s.x
    y += s.y
  }
  if (!x && !y) return r
  return new DOMRect(r.left - x, r.top - y, r.width, r.height)
}

function reduced(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

/** What moves: the column's rows (not the image's own, which goes with it) and the image itself. */
function movers(id: string): Map<string, HTMLElement> {
  const fig = figureOf(id)
  const out = new Map<string, HTMLElement>()
  if (!fig) return out
  const own = fig.closest('[data-row]')
  for (const el of columnRows(fig.closest('.wr-column') ?? document)) {
    if (el === own || el.contains(fig)) continue
    const key = el.getAttribute('data-row') ?? `cols:${el.getAttribute('data-cols')}`
    out.set(`row:${key}`, el)
  }
  out.set('figure', fig)
  return out
}

export type LayoutSnapshot = Map<string, DOMRect>

/** Where everything is painted now (mid-animation included), before a move. */
export function snapshotLayout(id: string): LayoutSnapshot | null {
  if (reduced() || typeof Element === 'undefined' || typeof Element.prototype.animate !== 'function') return null
  const out: LayoutSnapshot = new Map()
  movers(id).forEach((el, key) => out.set(key, el.getBoundingClientRect()))
  return out
}

/**
 * After the move has rendered: slide everything from where it was painted
 * (`before`) to where it's laid out now. A move made mid-glide starts from
 * where the glide had got to, so a quick drag stays one smooth motion.
 */
export function playFlip(before: LayoutSnapshot | null, id: string) {
  if (!before) return
  const els = movers(id)
  // Stop what's playing first, so the new layout is measured at rest.
  els.forEach((el) => el.getAnimations?.().forEach((a) => a.id === FLIP_ID && a.cancel()))
  const after = new Map<string, DOMRect>()
  els.forEach((el, key) => after.set(key, el.getBoundingClientRect()))
  els.forEach((el, key) => {
    const a = before.get(key)
    const b = after.get(key)
    if (!a || !b || b.width <= 0 || b.height <= 0) return
    const dx = a.left - b.left
    const dy = a.top - b.top
    // The image also changes size (a 250px wrap to the column's width); rows only move.
    const scale = key === 'figure' && a.width > 0 ? `scale(${a.width / b.width}, ${a.height / b.height})` : ''
    if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && (!scale || (Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5))) return
    const anim = el.animate(
      [
        { transform: `translate(${dx}px, ${dy}px) ${scale}`.trim(), transformOrigin: '0 0' },
        { transform: 'none', transformOrigin: '0 0' },
      ],
      { duration: FLIP_MS, easing: FLIP_EASE },
    )
    anim.id = FLIP_ID
  })
}
